/**
 * Personal property settings: the visited flag and your GDV override, with
 * the two independent revisions that fence them. One module serves the
 * register cards, the aside, Diligence (your GDV beside the evidence) and
 * Financial (your GDV in the breakdown), so every saved view refreshes the
 * same way after a save.
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, ApiError } from './http'
import { keys, useAccess } from './queries'
import { refreshLotResults } from './refurbishment'
import type { PersonalSettings } from './types'

const PERSONAL_FIELDS = ['contractVersion', 'propertyId', 'gdvOverridePence', 'gdvRevision', 'visited', 'visitRevision']
const revisionValid = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
const gdvValid = (value: unknown) => value === null || (typeof value === 'number' && Number.isSafeInteger(value) && value > 0)

export function parsePersonalSettings(value: unknown, propertyId?: string): PersonalSettings {
  const fail = (message: string): never => { throw new ApiError(502, 'property_personal_settings_contract_mismatch', message) }
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail('Personal property settings must be an object.')
  const raw = value as Record<string, unknown>
  if (Object.keys(raw).some(key => !PERSONAL_FIELDS.includes(key)) || PERSONAL_FIELDS.some(key => !(key in raw))) fail('Personal property settings contain missing or unsupported fields.')
  if (raw.contractVersion !== 'property-personal-settings-v1' || typeof raw.propertyId !== 'string' || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(raw.propertyId) || (propertyId !== undefined && raw.propertyId !== propertyId)) fail('Personal property settings have an invalid identity.')
  if (!gdvValid(raw.gdvOverridePence) || !revisionValid(raw.gdvRevision) || !revisionValid(raw.visitRevision) || typeof raw.visited !== 'boolean') fail('Personal property settings have invalid values or revisions.')
  return { contractVersion: 'property-personal-settings-v1', propertyId: raw.propertyId as string, gdvOverridePence: raw.gdvOverridePence as number | null, gdvRevision: raw.gdvRevision as number, visited: raw.visited as boolean, visitRevision: raw.visitRevision as number }
}

/** The two independent revisions prevent an older snapshot from undoing another edit. */
export function mergePersonalSettings(current: PersonalSettings | undefined, incoming: PersonalSettings): PersonalSettings {
  if (!current || current.propertyId !== incoming.propertyId) return incoming
  return {
    ...incoming,
    ...(current.gdvRevision > incoming.gdvRevision ? { gdvOverridePence: current.gdvOverridePence, gdvRevision: current.gdvRevision } : {}),
    ...(current.visitRevision > incoming.visitRevision ? { visited: current.visited, visitRevision: current.visitRevision } : {}),
  }
}

const readPersonal = async (propertyId: string, signal?: AbortSignal) => parsePersonalSettings((await api<{ data: unknown }>('personalSettings', { params: { propertyId }, signal }))?.data, propertyId)

/** Personal access: read/write the workspace settings and see valuations. */
export function usePersonalAccess() {
  const access = useAccess()
  return { read: access.capability('workspace.settings.read'), write: access.capability('workspace.settings.write'), valuation: access.capability('property.valuation.read') }
}

/** The saved personal settings; seeded from a bundle/card snapshot, fetched fresh only when asked. */
export function usePersonalSettings(propertyId: string, snapshot: PersonalSettings | null | undefined, fetch = false) {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: keys.personal(propertyId),
    queryFn: ({ signal }) => readPersonal(propertyId, signal),
    initialData: snapshot ?? undefined,
    enabled: fetch && propertyId.length > 0,
    retry: false,
    staleTime: 30_000,
  })
  useEffect(() => {
    if (snapshot) client.setQueryData<PersonalSettings>(keys.personal(propertyId), current => mergePersonalSettings(current, snapshot))
  }, [client, propertyId, snapshot])
  return query
}

/** Every saved view that reads your GDV: cards, pins, the bundle, diligence, deals-v2 results, variables, Financial. */
export function refreshPersonalViews(client: QueryClient, propertyId: string) {
  return Promise.all([
    refreshLotResults(client, propertyId),
    client.invalidateQueries({ queryKey: keys.property(propertyId) }),
    client.invalidateQueries({ queryKey: ['calculationVariables', propertyId] }),
    client.invalidateQueries({ predicate: query => { const head = query.queryKey[0]; return typeof head === 'string' && (head.startsWith('dealsV2') || head.startsWith('financial')) } }),
  ])
}

/** Save or clear your GDV for this property. Synchronous on the server; the saved views refresh after. */
export function usePatchPersonalGdv(propertyId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationKey: ['personalSettings', propertyId, 'gdv'],
    mutationFn: async (input: { gdvOverridePence: number | null; expectedGdvRevision: number }) => {
      if (!gdvValid(input.gdvOverridePence) || !revisionValid(input.expectedGdvRevision)) throw new Error('Enter a positive amount with at most two decimal places.')
      return parsePersonalSettings((await api<{ data: unknown }>('patchPersonalSettings', { params: { propertyId }, body: input }))?.data, propertyId)
    },
    onMutate: async () => { await client.cancelQueries({ queryKey: keys.personal(propertyId) }) },
    onSuccess: async (saved) => {
      client.setQueryData<PersonalSettings>(keys.personal(saved.propertyId), current => mergePersonalSettings(current, saved))
      await refreshPersonalViews(client, propertyId)
    },
    onError: async () => {
      // A response can be lost after the server commits. Reconcile; never retry a stale write.
      await client.fetchQuery({ queryKey: keys.personal(propertyId), queryFn: ({ signal }) => readPersonal(propertyId, signal) }).catch(() => undefined)
      await refreshPersonalViews(client, propertyId)
    },
  })
}
